// 世界场景玩家模块
import { GAME_CONSTANTS } from './GameConstants.js';
import DebugHelper from './DebugHelper.js';
import PlayerDefeatHandler from './PlayerDefeatHandler.js';
import VehicleEquipmentSystem from './VehicleEquipmentSystem.js';
import CorrosionLayerSystem from './CorrosionLayerSystem.js';
import GameStateStore from './GameStateStore.js';
import { playSceneSfx, playSceneBgm } from './LazyAssetLoader.js';
import SettingsHelper from './SettingsHelper.js';
import {
    ensurePlayerAnimations,
    getPlayerAnimKey,
    getPlayerStaticFrame
} from './PlayerSpriteFrames.js';
import { isCave, isTown } from './SceneClassHelper.js';

export default class WorldPlayer {
    constructor(scene) {
        this.scene = scene;
        this.playerSprite = null;
        this.vehicleSprite = null;
        this.playerElement = 'player1';
        
        // 玩家动画状态
        this.playerAnimations = {
            isMoving: false,
            currentDirection: 'down'
        };
        
        // 战车动画状态
        this.vehicleAnimations = {
            isMoving: false,
            currentDirection: 'down',
            speedMultiplier: GAME_CONSTANTS.PLAYER.VEHICLE_SPEED_MULTIPLIER,
            frameMapping: {
                down: { start: 0, end: 3 },
                left: { start: 4, end: 7 },
                right: { start: 8, end: 11 },
                up: { start: 12, end: 15 }
            },
            staticFrames: null
        };
        
        // 跟随设置
        this.followSettings = {
            enabled: true,
            alphaWhenFollowing: 0.3,
            alphaWhenRiding: 1.0,
            followSpeed: 0.2 // 跟随速度（0-1）
        };
        
        // 抗腐蚀图层系统：跟踪上次位置
        this.lastTilePosition = {
            x: null,
            y: null
        };
        
        // CP耗尽提示标志（只在第一次提示）
        this.hasShownCpDepletedWarning = false;
    }
    
    // 创建玩家角色
    createPlayerCharacter(playerData) {
        try {
            // 验证并设置默认的玩家元素（纹理键）
            let element = playerData.element;
            if (!element || element.trim() === '') {
                DebugHelper.warn('WorldPlayer: playerData.element 为空，使用默认值 player1');
                element = 'player1';
                // 更新 playerData 以便后续使用
                playerData.element = element;
            }
            
            // 检查纹理是否存在
            if (!this.scene.textures.exists(element)) {
                DebugHelper.warn(`WorldPlayer: 纹理 "${element}" 不存在，尝试使用 player1`);
                element = 'player1';
                playerData.element = element;
                
                // 如果 player1 也不存在，报错
                if (!this.scene.textures.exists(element)) {
                    DebugHelper.error('WorldPlayer: 默认纹理 player1 也不存在，无法创建玩家角色');
                    return null;
                }
            }
            
            this.playerSprite = this.scene.physics.add.sprite(playerData.x, playerData.y, element);
            this._applyPlayerSpriteSizing(this.playerSprite, element);
            this.playerSprite.setDepth(10);
            
            // 设置玩家物理属性
            if (this.playerSprite.body) {
                this.playerSprite.body.setCollideWorldBounds(false);
                this.playerSprite.body.setImmovable(false);
                this.playerSprite.body.setBounce(0, 0);
            }
            
            // 初始化玩家动画
            this.setupPlayerAnimations(playerData);
            
        } catch (error) {
            DebugHelper.error('WorldPlayer: 玩家图片资源加载失败', error);
            return null;
        }
        
        DebugHelper.log('WorldPlayer: 玩家角色创建完成');
        return this.playerSprite;
    }
    
    // 设置玩家动画
    setupPlayerAnimations(playerData) {
        if (!this.playerSprite) return;
        
        // 验证元素（纹理键）是否存在
        let element = playerData.element;
        if (!element || element.trim() === '') {
            DebugHelper.warn('WorldPlayer: setupPlayerAnimations - playerData.element 为空，使用默认值 player1');
            element = 'player1';
        }
        
        // 检查纹理是否存在
        if (!this.scene.textures.exists(element)) {
            DebugHelper.warn(`WorldPlayer: setupPlayerAnimations - 纹理 "${element}" 不存在，使用 player1`);
            element = 'player1';
        }
        
        // 如果纹理仍然不存在，无法创建动画
        if (!this.scene.textures.exists(element)) {
            DebugHelper.error('WorldPlayer: setupPlayerAnimations - 无法找到有效的玩家纹理，跳过动画创建');
            return;
        }
        
        try {
            this.playerElement = element;
            ensurePlayerAnimations(this.scene, element);
            this.playerSprite.setFrame(getPlayerStaticFrame(element, 'down'));
        } catch (error) {
            DebugHelper.error('WorldPlayer: setupPlayerAnimations - 创建动画失败', error);
        }
    }

    // 设置战车动画
    setupVehicleAnimations(tanks) {
        if (!this.vehicleSprite) return;
        
        const hasVehicleSpritesheet = this.scene.textures.exists(tanks.name);
        
        if (hasVehicleSpritesheet) {
            DebugHelper.log('WorldPlayer: 检测到战车精灵图，创建4方向动画');
            
            try {
                const { frameMapping } = this.vehicleAnimations;
                
                // 创建4个方向的战车动画
                if (!this.scene.anims.exists('vehicle_down')) {
                    this.scene.anims.create({
                        key: 'vehicle_down',
                        frames: this.scene.anims.generateFrameNumbers(tanks.name, {
                            start: frameMapping.down.start,
                            end: frameMapping.down.end
                        }),
                        frameRate: 8,
                        repeat: -1
                    });
                }
                
                if (!this.scene.anims.exists('vehicle_left')) {
                    this.scene.anims.create({
                        key: 'vehicle_left',
                        frames: this.scene.anims.generateFrameNumbers(tanks.name, {
                            start: frameMapping.left.start,
                            end: frameMapping.left.end
                        }),
                        frameRate: 8,
                        repeat: -1
                    });
                }
                
                if (!this.scene.anims.exists('vehicle_right')) {
                    this.scene.anims.create({
                        key: 'vehicle_right',
                        frames: this.scene.anims.generateFrameNumbers(tanks.name, {
                            start: frameMapping.right.start,
                            end: frameMapping.right.end
                        }),
                        frameRate: 8,
                        repeat: -1
                    });
                }
                
                if (!this.scene.anims.exists('vehicle_up')) {
                    this.scene.anims.create({
                        key: 'vehicle_up',
                        frames: this.scene.anims.generateFrameNumbers(tanks.name, {
                            start: frameMapping.up.start,
                            end: frameMapping.up.end
                        }),
                        frameRate: 8,
                        repeat: -1
                    });
                }
                
                DebugHelper.log('WorldPlayer: 战车4方向动画创建完成');
                
            } catch (error) {
                DebugHelper.error('WorldPlayer: 战车动画创建失败', error);
                this.setupVehicleStaticFrames();
            }
        } else {
            DebugHelper.log('WorldPlayer: 未找到战车精灵图，使用静态图像');
            this.setupVehicleStaticFrames();
        }
    }
    
    // 设置战车静态帧
    setupVehicleStaticFrames() {
        if (!this.vehicleSprite) return;
        
        this.vehicleAnimations.staticFrames = {
            down: 0,
            left: 4, 
            right: 8,
            up: 12
        };
        
        // 初始设置为向下帧
        this.vehicleSprite.setFrame(0);
    }
    
    // 创建战车（始终跟随玩家）
    createVehicle(playerX, playerY, tanks) {
        // 始终创建在玩家位置
        const vehicleX = playerX;
        const vehicleY = playerY;
        
        DebugHelper.log('WorldPlayer: 创建跟随战车...');
        
        try {
            const hasVehicleSpritesheet = this.scene.textures.exists(tanks.name);
            
            if (hasVehicleSpritesheet) {
                DebugHelper.log('WorldPlayer: 使用战车精灵图创建');
                this.vehicleSprite = this.scene.physics.add.sprite(vehicleX, vehicleY, tanks.name);
                this._applyPlayerSpriteSizing(this.vehicleSprite, tanks.name);
                this.vehicleSprite.setDepth(9);
                this.vehicleSprite.setFrame(0);
                
                // 设置战车物理属性
                if (this.vehicleSprite.body) {
                    this.vehicleSprite.body.setCollideWorldBounds(false);
                    this.vehicleSprite.body.setImmovable(false);
                    this.vehicleSprite.body.setBounce(0, 0);
                    // 战车不参与物理碰撞（不会阻挡玩家）
                    // this.vehicleSprite.body.checkCollision.none = true;
                }
            }
            
        } catch (error) {
            DebugHelper.warn('WorldPlayer: 战车创建失败，使用默认几何图形', error.message);
            this.vehicleSprite = this.scene.physics.add.rectangle(vehicleX, vehicleY, 48, 36, 0x808080);
            this.vehicleSprite.setOrigin(0.5);
        }
        
        // 初始化战车动画
        this.setupVehicleAnimations(tanks);
        
        // 根据当前上下车状态设置显示
        this.updateVehicleAppearance();
        
        DebugHelper.log('WorldPlayer: 跟随战车创建完成');
        return this.vehicleSprite;
    }
    
    // 更新战车外观（根据上下车状态）
    updateVehicleAppearance() {
        if (!this.vehicleSprite) return;
        
        if (this.scene.inVehicle) {
            // 上车状态：战车不透明，播放动画
            this.vehicleSprite.setVisible(true);
        } else {
            // 下车状态：战车半透明，停止动画
            this.vehicleSprite.setVisible(false);
        }
    }
    
    // 更新战车动画
    updateVehicleAnimations() {
        if (!this.vehicleSprite || !this.scene.inVehicle) return;
        
        const animMap = {
            'up': 'vehicle_up',
            'down': 'vehicle_down',
            'left': 'vehicle_left',
            'right': 'vehicle_right'
        };
        
        const targetAnim = animMap[this.vehicleAnimations.currentDirection];
        
        if (targetAnim && this.scene.anims.exists(targetAnim)) {
            if (!this.vehicleSprite.anims.isPlaying || 
                this.vehicleSprite.anims.currentAnim?.key !== targetAnim) {
                this.vehicleSprite.play(targetAnim, true);
            }
        } else if (this.vehicleAnimations.staticFrames) {
            const frameIndex = this.vehicleAnimations.staticFrames[this.vehicleAnimations.currentDirection];
            if (frameIndex !== undefined) {
                this.vehicleSprite.setFrame(frameIndex);
            }
        }
    }
    
    // 更新战车静态帧（下车时）
    updateVehicleStaticFrame() {
        if (!this.vehicleSprite || this.scene.inVehicle) return;
        
        if (this.vehicleAnimations.staticFrames) {
            const frameIndex = this.vehicleAnimations.staticFrames[this.vehicleAnimations.currentDirection];
            if (frameIndex !== undefined) {
                this.vehicleSprite.setFrame(frameIndex);
            }
        }
    }
    
    // 更新玩家动画
    updatePlayerAnimations() {
        if (!this.playerSprite || this.scene.inVehicle) return;
        
        if (this.playerAnimations.isMoving) {
            const direction = this.playerAnimations.currentDirection;
            const targetAnim = getPlayerAnimKey(this.playerElement, direction);
            if (targetAnim) {
                // 检查动画是否存在
                if (!this.scene.anims.exists(targetAnim)) {
                    DebugHelper.warn(`WorldPlayer: 动画 "${targetAnim}" 不存在，跳过播放`);
                    return;
                }
                
                // 检查动画是否正在播放，或者需要切换动画
                const isPlaying = this.playerSprite.anims.isPlaying;
                const currentAnimKey = this.playerSprite.anims.currentAnim?.key;
                
                if (!isPlaying || currentAnimKey !== targetAnim) {
                    try {
                        this.playerSprite.play(targetAnim, true);
                    } catch (error) {
                        DebugHelper.error(`WorldPlayer: 播放动画 "${targetAnim}" 失败`, error);
                    }
                }
            }
        } else {
            this.playerSprite.anims.stop();
            this.playerSprite.setFrame(
                getPlayerStaticFrame(this.playerElement, this.playerAnimations.currentDirection)
            );
        }
    }
    
    /**
     * 获取当前方向
     * @returns {string} 当前方向
     */
    getCurrentDirection() {
        if (this.scene.inVehicle) {
            return this.vehicleAnimations.currentDirection || 'down';
        } else {
            return this.playerAnimations.currentDirection || 'down';
        }
    }
    
    /**
     * 播放受伤动画（闪烁效果）
     */
    playDamageAnimation() {
        const targetSprite = this.scene.inVehicle ? this.vehicleSprite : this.playerSprite;
        if (!targetSprite) return;
        
        // 如果已经有闪烁动画在运行，不重复播放
        if (targetSprite._isFlashing) return;
        targetSprite._isFlashing = true;
        
        // 闪烁动画：快速改变alpha值（0.3 -> 1.0 -> 0.3 -> 1.0）
        this.scene.tweens.add({
            targets: targetSprite,
            alpha: 0.3,
            duration: 100,
            yoyo: true,
            repeat: 5, // 闪烁6次（3个完整周期）
            onComplete: () => {
                targetSprite.setAlpha(1.0); // 确保最终alpha为1.0
                targetSprite._isFlashing = false;
            }
        });
    }
    
    // 更新玩家移动
    updatePlayerMovement(cursors, baseSpeed) {
        if (!this.playerSprite) {
            DebugHelper.warn('WorldPlayer: 玩家精灵不存在，无法更新移动');
            return;
        }
        
        // 如果在战车中，检查必须装备和重量限制
        if (this.scene.inVehicle) {
            const playerData = this.scene.player || GameStateStore.getPlayer();
            const vehicle = playerData?.vehicle;
            if (vehicle) {
                const moveCheck = VehicleEquipmentSystem.checkCanMove(vehicle);
                if (!moveCheck.canMove) {
                    // 必须装备缺失、损坏或超重，显示错误信息并强制下车
                    this.scene.worldUI.showMessage(`战车无法移动！${moveCheck.message}`);
                    this.toggleVehicle(playerData, vehicle);
                    return;
                }
            }
        }
        
        const actualSpeed = this.scene.inVehicle ? 
            baseSpeed * this.vehicleAnimations.speedMultiplier : 
            baseSpeed;
        
        // 重置速度（添加安全检查）
        if (this.playerSprite && this.playerSprite.body) {
            this.playerSprite.setVelocity(0);
        }
        if (this.vehicleSprite && this.vehicleSprite.body) {
            this.vehicleSprite.setVelocity(0);
        }
        
        let moved = false;
        let velX = 0;
        let velY = 0;
        let primaryDirection = this.scene.inVehicle ? 
            this.vehicleAnimations.currentDirection : 
            this.playerAnimations.currentDirection;
        
        // 处理输入
        if (cursors.left.isDown) {
            velX = -actualSpeed;
            primaryDirection = 'left';
            moved = true;
        } else if (cursors.right.isDown) {
            velX = actualSpeed;
            primaryDirection = 'right';
            moved = true;
        }
        
        if (cursors.up.isDown) {
            velY = -actualSpeed;
            if (velX === 0) primaryDirection = 'up';
            moved = true;
        } else if (cursors.down.isDown) {
            velY = actualSpeed;
            if (velX === 0) primaryDirection = 'down';
            moved = true;
        }
        
        if (velX !== 0 && velY !== 0) {
            const diagonalSpeed = actualSpeed * 0.707;
            velX = velX > 0 ? diagonalSpeed : -diagonalSpeed;
            velY = velY > 0 ? diagonalSpeed : -diagonalSpeed;
        }
        
        // 应用速度
        if (this.scene.inVehicle && this.vehicleSprite?.body) {
            // 上车状态：战车移动，玩家隐藏
            this.vehicleSprite.setVelocityX(velX);
            this.vehicleSprite.setVelocityY(velY);
            
            // 玩家位置始终与战车同步
            if (this.playerSprite) {
                this.playerSprite.x = this.vehicleSprite.x;
                this.playerSprite.y = this.vehicleSprite.y;
            }
            
            // 更新战车动画
            this.vehicleAnimations.isMoving = moved;
            this.vehicleAnimations.currentDirection = primaryDirection;
            this.updateVehicleAnimations();
            
        } else if (this.playerSprite?.body) {
            // 下车状态：玩家移动
            this.playerSprite.setVelocityX(velX);
            this.playerSprite.setVelocityY(velY);
            
            // 更新玩家动画
            this.playerAnimations.isMoving = moved;
            this.playerAnimations.currentDirection = primaryDirection;
            this.updatePlayerAnimations();
        }
        
        // 处理抗腐蚀图层消耗（在移动后）
        if (moved) {
            this.processCorrosionLayerConsumption();
        }
    }
    
    /**
     * 处理抗腐蚀图层消耗
     */
    processCorrosionLayerConsumption() {
        const playerData = this.scene.player || GameStateStore.getPlayer();
        if (!playerData) return;
        
        // 检查是否是洞穴场景
        const isCaveScene = isCave(this.scene.config);
        if (!isCaveScene) return;
        
        // 计算当前瓦片坐标
        const currentSprite = this.scene.inVehicle ? this.vehicleSprite : this.playerSprite;
        if (!currentSprite) return;
        
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const currentTileX = Math.floor(currentSprite.x / tileSize);
        const currentTileY = Math.floor(currentSprite.y / tileSize);
        
        // 如果上次位置未初始化，初始化它
        if (this.lastTilePosition.x === null || this.lastTilePosition.y === null) {
            this.lastTilePosition.x = currentTileX;
            this.lastTilePosition.y = currentTileY;
            return;
        }
        
        // 检查位置是否改变
        if (this.lastTilePosition.x !== currentTileX || this.lastTilePosition.y !== currentTileY) {
            // 处理图层消耗
            const result = CorrosionLayerSystem.processMovement(
                playerData,
                this.lastTilePosition.x,
                this.lastTilePosition.y,
                currentTileX,
                currentTileY,
                isCaveScene
            );
            
            // 如果CP为0需要强制下车
            if (result.forceExitVehicle && this.scene.inVehicle) {
                const playerData = this.scene.player || GameStateStore.getPlayer();
                const vehicle = playerData?.vehicle;
                if (vehicle) {
                    // 只在第一次提示CP耗尽
                    if (!this.hasShownCpDepletedWarning) {
                        this.scene.worldUI?.showMessage('CP已耗尽！强制下车', '#ff0000');
                        this.hasShownCpDepletedWarning = true;
                    }
                    // 强制下车（副本中也允许）
                    this.toggleVehicle(playerData, vehicle);
                    // 更新位置记录，避免重复触发
                    this.lastTilePosition.x = currentTileX;
                    this.lastTilePosition.y = currentTileY;
                    return;
                }
            }
            
            // 如果玩家受到伤害（在副本中CP为0时掉血）
            if (result.playerDamaged) {
                const playerData = this.scene.player || GameStateStore.getPlayer();
                // 播放受伤动画（闪烁效果）
                this.playDamageAnimation();
                // 更新HUD显示（确保顶部血量显示更新）
                if (this.scene.worldUI && playerData) {
                    this.scene.worldUI.updateHUD(playerData);
                }
                
                // 检查HP是否为0，如果为0则传送到城镇出生点
                if (playerData.hp <= 0) {
                    PlayerDefeatHandler.handlePlayerDefeated(this.scene);
                    return;
                }
                
                // 不显示对话框消息，避免频繁弹出
                // 只更新位置记录，继续移动
                this.lastTilePosition.x = currentTileX;
                this.lastTilePosition.y = currentTileY;
                return;
            }
            
            // 显示其他消息（图层消耗等）
            if (result.consumed && result.message && !result.playerDamaged) {
                this.scene.worldUI?.showMessage(result.message);
            }
            
            if (result.damagedEquipment && result.damagedEquipment.length > 0) {
                result.damagedEquipment.forEach(damaged => {
                    this.scene.worldUI?.showMessage(damaged.message);
                });
            }
            
            // 只要处理了移动（无论是否消耗），都更新HUD显示（确保CP值实时更新）
            if (result.success && this.scene.worldUI && playerData) {
                this.scene.worldUI.updateHUD(playerData);
            }
            
            // 更新上次位置
            this.lastTilePosition.x = currentTileX;
            this.lastTilePosition.y = currentTileY;
        }
    }
    
    // 上车/下车切换
    toggleVehicle(playerData, tankData) {
        // 检查场景类型
        const sceneConfig = this.scene.worldMapJson?.sceneConfig;
        const sceneKey = this.scene.scene.key;
        const cave = isCave(sceneConfig);
        const town = isTown(sceneConfig, sceneKey);
        
        playSceneSfx(this.scene, 'inVehicle');
        if (this.scene.inVehicle) {
            // 下车
            // 副本中允许下车
            this.scene.inVehicle = false;
            this.playerSprite.setVisible(true);
            this.playerSprite.setAlpha(1);
            
            // 同步位置：玩家在战车位置
            if (this.vehicleSprite) {
                this.playerSprite.x = this.vehicleSprite.x;
                this.playerSprite.y = this.vehicleSprite.y;
                
                // 同步方向
                this.playerAnimations.currentDirection = this.vehicleAnimations.currentDirection;
                
                // 更新战车外观
                this.updateVehicleAppearance();
                this.updateVehicleStaticFrame();
            }
            
            // 更新游戏状态
            playerData.inVehicle = false;
            GameStateStore.patchPlayer({ inVehicle: false });
            
            this.scene.worldUI.showMessage('你下车了');
            playSceneBgm(this.scene, 'world_bgm1', { volume: SettingsHelper.getBGMPlayVolume() });
            
        } else {
            // 上车
            // 在城镇中不能上车
            if (town) {
                this.scene.worldUI.showMessage('城镇中不能上车！', '#ff0000');
                return;
            }
            
            // 检查必须装备
            const vehicle = playerData.vehicle || tankData;
            if (vehicle) {
                const canBoard = VehicleEquipmentSystem.canBoard(vehicle);
                if (!canBoard.canBoard) {
                    this.scene.worldUI.showMessage(canBoard.message);
                    return;
                }
            }
            
            // 上车 - 不再检查距离，因为战车一直在身边
            this.scene.inVehicle = true;
            this.playerSprite.setVisible(false);
            
            if (this.vehicleSprite) {
                // 同步位置：战车在玩家位置
                this.vehicleSprite.x = this.playerSprite.x;
                this.vehicleSprite.y = this.playerSprite.y;
                
                // 同步方向
                this.vehicleAnimations.currentDirection = this.playerAnimations.currentDirection;
                
                // 更新战车外观
                this.updateVehicleAppearance();
                this.updateVehicleAnimations();
            }
            
            // 更新游戏状态
            playerData.inVehicle = true;
            GameStateStore.patchPlayer({ inVehicle: true });
            
            this.scene.worldUI.showMessage('你上了战车，移动速度提升了！');
            playSceneBgm(this.scene, 'world_bgm2', { volume: SettingsHelper.getBGMPlayVolume() });
        }
        
        // 更新玩家动画状态
        this.updatePlayerAnimations();
        
        // 更新HUD显示（上/下车后需要更新显示的信息）
        if (this.scene.worldUI && playerData) {
            this.scene.worldUI.updateHUD(playerData);
        }
    }
    
    // 获取当前控制的对象
    getCurrentControlledSprite() {
        return this.scene.inVehicle ? this.vehicleSprite : this.playerSprite;
    }

    /**
     * 按精灵帧尺寸设置显示缩放与碰撞体（128px 新素材缩放到 32px 地砖比例）
     */
    _applyPlayerSpriteSizing(sprite, element) {
        const texture = this.scene.textures.get(element);
        const frame = texture?.get();
        const frameW = frame?.width || 32;

        if (frameW > 64) {
            const scale = 32 / frameW;
            sprite.setScale(scale);
            const hit = Math.round(28 / scale);
            sprite.setSize(hit, hit);
            sprite.setOffset(Math.round(2 / scale), Math.round(2 / scale));
        } else {
            sprite.setScale(1);
            sprite.setSize(28, 28);
            sprite.setOffset(2, 2);
        }
    }
    
    // 更新战车跟随（在场景update中调用）
    updateVehicleFollowing() {
        if (!this.vehicleSprite || !this.playerSprite || this.scene.inVehicle) return;
        
        // 下车状态：战车平滑跟随玩家
        const targetX = this.playerSprite.x;
        const targetY = this.playerSprite.y;
        const currentX = this.vehicleSprite.x;
        const currentY = this.vehicleSprite.y;
        
        // 计算距离
        const distance = Phaser.Math.Distance.Between(currentX, currentY, targetX, targetY);
        
        if (distance > 5) { // 只有距离大于5像素时才移动
            // 线性插值跟随
            const lerpX = Phaser.Math.Linear(currentX, targetX, this.followSettings.followSpeed);
            const lerpY = Phaser.Math.Linear(currentY, targetY, this.followSettings.followSpeed);
            
            this.vehicleSprite.x = lerpX;
            this.vehicleSprite.y = lerpY;
            
            // 根据移动方向更新战车静态帧
            const dx = targetX - currentX;
            const dy = targetY - currentY;
            
            if (Math.abs(dx) > Math.abs(dy)) {
                this.vehicleAnimations.currentDirection = dx > 0 ? 'right' : 'left';
            } else if (dy !== 0) {
                this.vehicleAnimations.currentDirection = dy > 0 ? 'down' : 'up';
            }
            
            this.updateVehicleStaticFrame();
        }
    }
    
    /**
     * 处理玩家被击败（HP为0）
     * 传送到城镇出生点
     */
    handlePlayerDefeated() {
        PlayerDefeatHandler.handlePlayerDefeated(this.scene);
    }
}